import type { SenderPermission } from '@features/integrations/api/contracts'
import type { ValueSelectorOption } from '@features/shared/components/ValueSelector'

/** An identity choice is presentation only, never a cached Exchange permission. */
export type MicrosoftSenderChoice =
  | SenderPermission
  | {
      source: 'identity'
      sender_email: string
      display_name: string
      is_own_mailbox: true
    }

function rank(sender: MicrosoftSenderChoice) {
  if (sender.is_own_mailbox) return 0
  const type = { user: 1, shared: 3, room: 5, equipment: 7, other: 9 }[sender.mailbox_type] ?? 9
  return type + (sender.send_as ? 0 : 1)
}

function permissionLabel(sender: MicrosoftSenderChoice) {
  if (sender.is_own_mailbox) return 'Myself'
  const mode = sender.send_as ? 'Send as' : 'Send on behalf of'
  const type = {
    user: '',
    shared: 'Shared mailbox',
    room: 'Room',
    equipment: 'Equipment',
    other: 'Mailbox',
  }[sender.mailbox_type]
  return type ? `${mode} · ${type}` : mode
}

export function microsoftSenderOptions(senders: MicrosoftSenderChoice[]): ValueSelectorOption[] {
  return [...senders]
    .sort(
      (a, b) =>
        rank(a) - rank(b) ||
        (a.display_name || a.sender_email).localeCompare(
          b.display_name || b.sender_email,
          undefined,
          { sensitivity: 'base' }
        ) ||
        a.sender_email.localeCompare(b.sender_email)
    )
    .map((sender) => ({
      id: sender.sender_email,
      label: sender.display_name || sender.sender_email,
      description:
        sender.display_name && sender.display_name !== sender.sender_email
          ? sender.sender_email
          : undefined,
      secondaryLabel: permissionLabel(sender),
      searchText: `${sender.display_name} ${sender.sender_email} ${permissionLabel(sender)}`,
    }))
}
